// Mini CRM: todas las cotizaciones (todos ven todo), con filtros por estado, vendedor, tipo, texto y fechas.
// Los filtros viven en la URL: un enlace copiado abre la misma lista.
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { CalendarClock, ChevronRight, Lock, OctagonAlert, Plus, RotateCcw, Search, SlidersHorizontal, Trash2 } from 'lucide-react';
import { DIAS_SIN_RESPUESTA, ESTADOS_COTIZACION, TIPOS_PROYECTO, fechaHermosillo, fechaHoraCorta, motivoNoMandaAPapelera, numero, puedeAbrirCotizacion } from '@cotizador/compartido';
import type { EstadoCotizacion, ResumenCotizacion, Semaforo, TipoProyecto, Usuario } from '@cotizador/compartido';
import { api } from '../api/consultas.ts';
import { Enlace, navegar, usarRuta } from '../ruteo.tsx';
import { usarUsuario } from '../sesion.tsx';
import { Boton, BotonConfirmar, Cargando, CampoSelect, CampoTexto, Chip, ErrorEnLinea, Vacio } from '../componentes/ui.tsx';
import { ETIQUETA_ESTADO, ETIQUETA_SEMAFORO, ETIQUETA_TIPO, dinero, kwp, plural } from '../logica/etiquetas.ts';

const ABIERTAS: readonly EstadoCotizacion[] = ['borrador', 'enviada', 'seguimiento', 'visita_agendada'];

export function tonoEstado(e: EstadoCotizacion): 'neutro' | 'prusia' | 'verde' | 'rojo' | 'ambar' {
  if (e === 'ganada') return 'verde';
  if (e === 'perdida' || e === 'cancelada') return 'neutro';
  if (e === 'borrador') return 'ambar';
  return 'prusia';
}

function fechaCorta(f: string): string {
  const [a, m, d] = f.split('-');
  const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  return `${Number(d)} ${meses[Number(m) - 1]}${a === fechaHermosillo().slice(0, 4) ? '' : ` ${a}`}`;
}

function Seguimiento({ fecha }: { fecha: string | null }) {
  if (!fecha) return null;
  const vencido = fecha < fechaHermosillo();
  const hoy = fecha === fechaHermosillo();
  return (
    <span className={['seguimiento', vencido ? 'seguimiento--vencido' : '', hoy ? 'seguimiento--hoy' : ''].join(' ')}>
      <CalendarClock aria-hidden />
      {vencido ? `Seguimiento vencido (${fechaCorta(fecha)})` : hoy ? 'Seguimiento hoy' : `Seguimiento ${fechaCorta(fecha)}`}
    </span>
  );
}

/** ADENDA A0b: enviada con 3 días o más sin cambio de estado → el cotizador la marca (el servidor cuenta los días). */
function SinRespuesta({ dias }: { dias: number | null | undefined }) {
  if (dias === null || dias === undefined || dias < DIAS_SIN_RESPUESTA) return null;
  return (
    <Chip tono="ambar" icono={<CalendarClock aria-hidden />}>
      {dias} días sin respuesta
    </Chip>
  );
}

const usdW = (n: number | null | undefined) => (n === null || n === undefined ? '—' : `${numero(n, 3)}`);
const pct1 = (n: number | null | undefined) => (n === null || n === undefined ? '—' : `${numero(n, 1)} %`);

/** Totales que solo ve Diego (11-oct): utilidad esperada del pipeline, por vendedor, por estado y por mes. */
function TotalesDiego({ lista }: { lista: ResumenCotizacion[] }) {
  const abiertas = lista.filter((c) => ABIERTAS.includes(c.estado));
  const suma = (xs: ResumenCotizacion[]) => xs.reduce((s, c) => s + (c.utilidad_sin_iva ?? 0), 0);
  const agrupar = (clave: (c: ResumenCotizacion) => string) => {
    const m = new Map<string, ResumenCotizacion[]>();
    for (const c of lista) m.set(clave(c), [...(m.get(clave(c)) ?? []), c]);
    return [...m.entries()].sort((a, b) => suma(b[1]) - suma(a[1]));
  };
  const tabla = (titulo: string, grupos: [string, ResumenCotizacion[]][]) => (
    <table className="tabla tabla--compacta">
      <caption className="tabla__titulo">{titulo}</caption>
      <tbody>
        {grupos.map(([k, xs]) => (
          <tr key={k}>
            <th scope="row">
              {k} <span className="tabla__secundario">({xs.length})</span>
            </th>
            <td className="num">{dinero(suma(xs))}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
  return (
    <details className="plegable totales-diego">
      <summary>
        Utilidad esperada sin IVA (solo tú): <strong>{dinero(suma(abiertas))}</strong> en {plural(abiertas.length, 'abierta', 'abiertas')}
      </summary>
      <div className="totales-diego__tablas">
        {tabla('Por vendedor', agrupar((c) => c.vendedor_nombre))}
        {tabla('Por estado', agrupar((c) => ETIQUETA_ESTADO[c.estado]))}
        {tabla('Por mes', agrupar((c) => c.creada_en.slice(0, 7)))}
      </div>
    </details>
  );
}

function Rojos({ n }: { n: number }) {
  if (!n) return null;
  return (
    <Chip tono="rojo" icono={<OctagonAlert aria-hidden />}>
      {n === 1 ? '1 rojo' : `${n} rojos`}
    </Chip>
  );
}

/** Tarjeta del celular: enlace si la puede abrir; si es de otro vendedor (externo, A0b), solo el renglón. */
function Renglon({ c, abre, children }: { c: ResumenCotizacion; abre: boolean; children: ReactNode }) {
  if (abre)
    return (
      <Enlace a={`/cotizaciones/${c.id}`} className="tarjeta-cot">
        {children}
      </Enlace>
    );
  return (
    <div className="tarjeta-cot tarjeta-cot--solo" aria-label={`${c.folio}: de otro vendedor, solo el renglón`}>
      {children}
    </div>
  );
}

/** Semáforo de margen de la recomendada en el CRM (Diego, 11-oct): SOLO el color (Diego ve además el % y la utilidad en sus
 *  columnas). El servidor no le manda el color a un externo; sin color, una raya. */
function PuntoSemaforo({ color }: { color: Semaforo | null }) {
  if (!color) return <span className="nota-suave">—</span>;
  return (
    <span className={`semaforo semaforo--${color}`} data-semaforo={color}>
      <span className="semaforo__punto" aria-hidden />
      {ETIQUETA_SEMAFORO[color]}
    </span>
  );
}

/** PAPELERA (Diego, 13-oct): quién la mandó a la papelera y cuándo (solo en la vista «Papelera» de Diego). */
function BorradaPor({ c }: { c: ResumenCotizacion }) {
  if (!c.papelera) return null;
  return (
    <span className="tabla__secundario">
      Borrada por {c.papelera.nombre} · {fechaHoraCorta(c.papelera.en)}
    </span>
  );
}

/** PAPELERA: «Borrar» (con la pregunta en dos toques) o, en la vista Papelera, «Recuperar». */
function AccionPapelera({
  c,
  enPapelera,
  puedeBorrar,
  esDiego,
  trabajando,
  alBorrar,
  alRecuperar,
}: {
  c: ResumenCotizacion;
  enPapelera: boolean;
  puedeBorrar: boolean;
  esDiego: boolean;
  trabajando: boolean;
  alBorrar: () => void;
  alRecuperar: () => void;
}) {
  if (enPapelera)
    return (
      <Boton chico variante="secundario" icono={<RotateCcw aria-hidden />} cargando={trabajando} aria-label={`Recuperar ${c.folio}`} onClick={alRecuperar}>
        Recuperar
      </Boton>
    );
  if (!puedeBorrar) return null;
  return (
    <BotonConfirmar
      pregunta={esDiego ? 'Se va a la papelera; la puedes recuperar.' : 'Se va a la papelera; Diego la puede recuperar.'}
      icono={<Trash2 aria-hidden />}
      etiquetaAccesible={`Borrar ${c.folio}`}
      cargando={trabajando}
      alConfirmar={alBorrar}
    >
      Borrar
    </BotonConfirmar>
  );
}

export function Cotizaciones() {
  const { consulta } = usarRuta();
  const { puede, usuario, veCostos, veSemaforo } = usarUsuario();
  const sinRespuesta = consulta.get('sin_respuesta') === 'si';
  // PAPELERA: solo Diego la ve (el servidor responde 403 a los demás); a cualquier otro, el parámetro no hace nada.
  const verPapelera = consulta.get('papelera') === 'si' && puede('administrar_papelera');
  const estado = (consulta.get('estado') ?? '') as EstadoCotizacion | '';
  const vendedor = consulta.get('vendedor') ?? '';
  const tipo = (consulta.get('tipo') ?? '') as TipoProyecto | '';
  const desde = consulta.get('desde') ?? '';
  const hasta = consulta.get('hasta') ?? '';
  const textoUrl = consulta.get('texto') ?? '';
  const [texto, setTexto] = useState(textoUrl);
  const [lista, setLista] = useState<ResumenCotizacion[] | null>(null);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [masFiltros, setMasFiltros] = useState(Boolean(desde || hasta));
  const [trabajando, setTrabajando] = useState<string | null>(null);
  const [errorAccion, setErrorAccion] = useState<unknown>(null);
  const [hecho, setHecho] = useState<string | null>(null);

  function poner(cambios: Record<string, string>) {
    const q = new URLSearchParams(consulta);
    for (const [k, v] of Object.entries(cambios)) {
      if (v) q.set(k, v);
      else q.delete(k);
    }
    const s = q.toString();
    navegar(`/cotizaciones${s ? `?${s}` : ''}`, { reemplazar: true });
  }

  // Búsqueda por texto: espera a que dejes de escribir.
  useEffect(() => {
    if (texto === textoUrl) return;
    const t = window.setTimeout(() => poner({ texto: texto.trim() }), 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texto]);

  useEffect(() => {
    let vivo = true;
    setError(null);
    api
      .cotizaciones({ estado, vendedor_id: vendedor || undefined, texto: textoUrl || undefined, desde: desde || undefined, hasta: hasta || undefined, sin_respuesta: sinRespuesta, papelera: verPapelera })
      .then((r) => vivo && setLista(r.cotizaciones))
      .catch((e: unknown) => vivo && setError(e));
    return () => {
      vivo = false;
    };
  }, [estado, vendedor, textoUrl, desde, hasta, sinRespuesta, verPapelera]);

  useEffect(() => {
    api
      .usuarios()
      .then((r) => setUsuarios(r.usuarios))
      .catch(() => setUsuarios([]));
  }, []);

  const visibles = useMemo(() => (lista ?? []).filter((c) => !tipo || c.tipo === tipo), [lista, tipo]);
  const abiertas = visibles.filter((c) => ABIERTAS.includes(c.estado));
  const contadoAbierto = abiertas.reduce((s, c) => s + (c.contado_recomendada ?? 0), 0);
  const rojos = visibles.reduce((s, c) => s + c.rojos_pendientes, 0);
  const hayFiltros = Boolean(estado || vendedor || tipo || textoUrl || desde || hasta || sinRespuesta || verPapelera);
  const vendedores = usuarios.filter((u) => u.rol !== 'tecnico');
  // Junta, segunda tanda (13-oct): «hecha por» (quien la capturó a nombre de otro vendedor), en chico. Nunca va al PDF.
  const hechaPor = (c: { vendedor_id: string; creada_por: string }) =>
    c.creada_por !== c.vendedor_id ? `hecha por ${usuarios.find((u) => u.id === c.creada_por)?.nombre ?? 'otra persona'}` : null;
  const conPorW = visibles.filter((c) => typeof c.contado_usd_por_w === 'number');
  const promedioUsdW = conPorW.length ? conPorW.reduce((s, c) => s + c.contado_usd_por_w!, 0) / conPorW.length : null;
  // Recordatorio A0b: las MÍAS enviadas con 3+ días sin respuesta (Diego ve todas en el filtro).
  const misSinRespuesta = (lista ?? []).filter((c) => (c.dias_sin_respuesta ?? 0) >= DIAS_SIN_RESPUESTA && c.vendedor_id === usuario.id);
  const abre = (c: ResumenCotizacion) => puedeAbrirCotizacion({ id: usuario.id, rol: usuario.rol, activo: true }, c);
  // PAPELERA: el botón «Borrar» sale con la MISMA regla que aplica el servidor (Diego cualquiera; un vendedor, sus borradores).
  const puedeBorrar = (c: ResumenCotizacion) => !verPapelera && motivoNoMandaAPapelera({ id: usuario.id, rol: usuario.rol, activo: true }, c) === null;
  const conAcciones = verPapelera || visibles.some(puedeBorrar);
  const esDiego = puede('administrar_papelera');

  function papelera(c: ResumenCotizacion, recuperar: boolean) {
    setTrabajando(c.id);
    setErrorAccion(null);
    setHecho(null);
    (recuperar ? api.recuperarCotizacion(c.id, c.revision) : api.mandarAPapelera(c.id, c.revision))
      .then(() => {
        // Sale de esta lista (la normal no trae las de la papelera; la papelera, las recuperadas): totales y conteo se ajustan solos.
        setLista((l) => (l ? l.filter((x) => x.id !== c.id) : l));
        setHecho(recuperar ? `${c.folio} salió de la papelera y volvió a la lista.` : `${c.folio} se fue a la papelera.${esDiego ? '' : ' Diego la puede recuperar.'}`);
      })
      .catch((e: unknown) => setErrorAccion(e))
      .finally(() => setTrabajando(null));
  }
  const accion = (c: ResumenCotizacion) => (
    <AccionPapelera
      c={c}
      enPapelera={verPapelera}
      puedeBorrar={puedeBorrar(c)}
      esDiego={esDiego}
      trabajando={trabajando === c.id}
      alBorrar={() => papelera(c, false)}
      alRecuperar={() => papelera(c, true)}
    />
  );

  return (
    <div className="pagina pagina--lista">
      <header className="pagina__cabeza">
        <div>
          <h1 className="pagina__titulo">Cotizaciones</h1>
          {lista && verPapelera && <p className="pagina__resumen">{plural(visibles.length, 'cotización', 'cotizaciones')} en la papelera · no cuentan en los totales</p>}
          {lista && !verPapelera && (
            <p className="pagina__resumen">
              {plural(visibles.length, 'cotización', 'cotizaciones')}
              {abiertas.length > 0 && (
                <>
                  {' · '}
                  {plural(abiertas.length, 'abierta', 'abiertas')} por <strong>{dinero(contadoAbierto)}</strong> al contado
                </>
              )}
              {rojos > 0 && <> · <span className="texto-rojo">{plural(rojos, 'aviso rojo', 'avisos rojos')}</span></>}
              {promedioUsdW !== null && <> · promedio <strong>{numero(promedioUsdW, 3)} USD/W</strong> de contado</>}
            </p>
          )}
        </div>
      </header>

      {misSinRespuesta.length > 0 && !sinRespuesta && !verPapelera && (
        <div className="aviso-ancho aviso-ancho--ambar" role="status">
          <p>
            <strong>{plural(misSinRespuesta.length, 'cotización tuya lleva', 'cotizaciones tuyas llevan')}</strong> {DIAS_SIN_RESPUESTA} días o más enviada{misSinRespuesta.length === 1 ? '' : 's'} sin respuesta. Dales seguimiento.
          </p>
          <button type="button" className="enlace-boton" onClick={() => poner({ sin_respuesta: 'si', vendedor: usuario.rol === 'director' ? vendedor : usuario.id })}>
            Ver cuáles
          </button>
        </div>
      )}
      {veCostos && lista && visibles.length > 0 && !verPapelera && <TotalesDiego lista={visibles} />}

      <div className="filtros" role="search">
        <div className="filtros__buscar">
          <Search aria-hidden className="filtros__lupa" />
          <input
            className="filtros__texto"
            type="search"
            placeholder="Buscar folio, cliente o ciudad"
            aria-label="Buscar folio, cliente o ciudad"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
          <button
            type="button"
            className={['icono-boton', masFiltros ? 'es-activo' : ''].join(' ')}
            aria-expanded={masFiltros}
            aria-label="Más filtros"
            title="Más filtros"
            onClick={() => setMasFiltros((v) => !v)}
          >
            <SlidersHorizontal aria-hidden />
          </button>
        </div>
        <div className="filtros__estados" role="group" aria-label="Estado">
          <button
            type="button"
            className={['pastilla', !estado && !sinRespuesta && !verPapelera ? 'es-activo' : ''].join(' ')}
            aria-pressed={!estado && !sinRespuesta && !verPapelera}
            onClick={() => poner({ estado: '', sin_respuesta: '', papelera: '' })}
          >
            Todas
          </button>
          <button type="button" className={['pastilla', sinRespuesta ? 'es-activo' : ''].join(' ')} aria-pressed={sinRespuesta} onClick={() => poner({ sin_respuesta: sinRespuesta ? '' : 'si', estado: '', papelera: '' })}>
            Sin respuesta ({DIAS_SIN_RESPUESTA}+ días)
          </button>
          {ESTADOS_COTIZACION.map((e) => (
            <button key={e} type="button" className={['pastilla', estado === e ? 'es-activo' : ''].join(' ')} aria-pressed={estado === e} onClick={() => poner({ estado: estado === e ? '' : e })}>
              {ETIQUETA_ESTADO[e]}
            </button>
          ))}
          {esDiego && (
            <button
              type="button"
              className={['pastilla', verPapelera ? 'es-activo' : ''].join(' ')}
              aria-pressed={verPapelera}
              onClick={() => poner({ papelera: verPapelera ? '' : 'si', sin_respuesta: '', estado: '' })}
            >
              <Trash2 aria-hidden className="icono-linea" /> Papelera
            </button>
          )}
        </div>
        {masFiltros && (
          <div className="filtros__mas">
            <CampoSelect
              etiqueta="Vendedor"
              value={vendedor}
              onChange={(e) => poner({ vendedor: e.target.value })}
              opciones={[{ valor: '', texto: 'Todos' }, ...vendedores.map((u) => ({ valor: u.id, texto: u.id === usuario.id ? `${u.nombre} (yo)` : u.nombre }))]}
            />
            <CampoSelect
              etiqueta="Tipo"
              value={tipo}
              onChange={(e) => poner({ tipo: e.target.value })}
              opciones={[{ valor: '', texto: 'Todos' }, ...TIPOS_PROYECTO.map((t) => ({ valor: t, texto: ETIQUETA_TIPO[t] }))]}
            />
            <CampoTexto etiqueta="Actualizada desde" type="date" value={desde} max={hasta || undefined} onChange={(e) => poner({ desde: e.target.value })} />
            <CampoTexto etiqueta="Hasta" type="date" value={hasta} min={desde || undefined} onChange={(e) => poner({ hasta: e.target.value })} />
            {hayFiltros && (
              <button
                type="button"
                className="enlace-boton filtros__limpiar"
                onClick={() => {
                  setTexto('');
                  navegar('/cotizaciones', { reemplazar: true });
                }}
              >
                Quitar filtros
              </button>
            )}
          </div>
        )}
      </div>

      <ErrorEnLinea error={error} />
      <ErrorEnLinea error={errorAccion} alCerrar={() => setErrorAccion(null)} />
      {hecho && (
        <p className="nota-suave" role="status">
          {hecho}
        </p>
      )}
      {!lista && !error && <Cargando renglones={6} etiqueta="Cargando cotizaciones" />}
      {lista && visibles.length === 0 && (
        <Vacio
          titulo={verPapelera ? 'La papelera está vacía' : hayFiltros ? 'Nada con esos filtros' : 'Todavía no hay cotizaciones'}
          accion={
            hayFiltros ? (
              <button type="button" className="boton boton--secundario" onClick={() => navegar('/cotizaciones', { reemplazar: true })}>
                Quitar filtros
              </button>
            ) : puede('editar_cotizaciones') ? (
              <Enlace a="/cotizaciones/nueva" className="boton boton--principal">
                <Plus aria-hidden />
                <span>Empezar la primera</span>
              </Enlace>
            ) : undefined
          }
        >
          {verPapelera ? 'Aquí aparecen las cotizaciones borradas; desde aquí las recuperas.' : hayFiltros ? 'Prueba con otro estado o vendedor.' : 'Cada cotización empieza con un cuestionario corto sobre el cliente y sus recibos.'}
        </Vacio>
      )}

      {visibles.length > 0 && (
        <>
          <ul className="lista-cot solo-celular">
            {visibles.map((c) => (
              <li key={c.id}>
                <Renglon c={c} abre={abre(c)}>
                  <div className="tarjeta-cot__arriba">
                    <span className="folio">{c.folio}</span>
                    <Chip tono={tonoEstado(c.estado)}>{ETIQUETA_ESTADO[c.estado]}</Chip>
                  </div>
                  <p className="tarjeta-cot__cliente">{c.cliente_nombre}</p>
                  <p className="tarjeta-cot__meta">
                    {c.multisitio ? `${c.multisitio.sitio} · ` : ''}
                    {ETIQUETA_TIPO[c.tipo]} · {c.ciudad} · {c.etapa === 'final' ? 'Final' : 'Precotización'}
                  </p>
                  <div className="tarjeta-cot__cifras">
                    <span className="tarjeta-cot__contado">{dinero(c.contado_recomendada)}</span>
                    <span className="tarjeta-cot__kwp">
                      {/* una ampliación de baterías no tiene kWp (no vende paneles) */}
                      {c.tipo === 'ampliacion_baterias' && c.kwp_recomendada === null ? 'Baterías' : kwp(c.kwp_recomendada)}
                      {typeof c.contado_usd_por_w === 'number' ? ` · ${usdW(c.contado_usd_por_w)} USD/W` : ''}
                      {veCostos && typeof c.margen_pct === 'number' ? ` · margen ${pct1(c.margen_pct)}` : ''}
                    </span>
                    {veSemaforo && <PuntoSemaforo color={c.semaforo_margen ?? null} />}
                  </div>
                  <div className="tarjeta-cot__abajo">
                    <span className="tarjeta-cot__vendedor">
                      {c.vendedor_nombre}
                      {hechaPor(c) ? ` · ${hechaPor(c)}` : ''}
                    </span>
                    <Rojos n={c.rojos_pendientes} />
                    <SinRespuesta dias={c.dias_sin_respuesta} />
                    <Seguimiento fecha={c.proximo_seguimiento} />
                    {!abre(c) && (
                      <span className="tarjeta-cot__solo-lista">
                        <Lock aria-hidden /> Solo el renglón
                      </span>
                    )}
                    <BorradaPor c={c} />
                  </div>
                </Renglon>
                {accion(c)}
              </li>
            ))}
          </ul>

          <div className="tabla-envoltura solo-escritorio">
            <table className="tabla tabla--cot">
              <thead>
                <tr>
                  <th scope="col">Folio</th>
                  <th scope="col">Cliente</th>
                  <th scope="col">Vendedor</th>
                  <th scope="col">Estado</th>
                  <th scope="col" className="num">
                    kWp
                  </th>
                  <th scope="col" className="num" title="Contado CON IVA de la recomendada (lo que paga el cliente)">
                    Contado
                  </th>
                  <th scope="col" className="num" title="Contado sin IVA de la recomendada entre sus watts">
                    USD/W
                  </th>
                  {veSemaforo && (
                    <th scope="col" title="Margen de la recomendada: verde 30 % o más, amarillo de 20 a 30 %, rojo debajo de 20 %">
                      Semáforo
                    </th>
                  )}
                  {veCostos && (
                    <>
                      <th scope="col" className="num" title="Margen sobre el precio SIN IVA (el Contado de la lista es con IVA)">
                        Margen sin IVA
                      </th>
                      <th scope="col" className="num" title="Utilidad sobre el precio SIN IVA (el Contado de la lista es con IVA)">
                        Utilidad sin IVA
                      </th>
                    </>
                  )}
                  <th scope="col">Avisos</th>
                  <th scope="col">Actualizada</th>
                  {conAcciones && <th scope="col">{verPapelera ? 'Papelera' : <span className="solo-lector">Acciones</span>}</th>}
                  <th scope="col">
                    <span className="solo-lector">Abrir</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((c) => (
                  <tr key={c.id} className={abre(c) ? 'tabla__fila-enlace' : 'tabla__fila-solo'} onClick={abre(c) ? () => navegar(`/cotizaciones/${c.id}`) : undefined}>
                    <td>
                      <span className="folio">{c.folio}</span>
                    </td>
                    <td>
                      {abre(c) ? (
                        <Enlace a={`/cotizaciones/${c.id}`} className="tabla__principal" onClick={(e) => e.stopPropagation()}>
                          {c.cliente_nombre}
                        </Enlace>
                      ) : (
                        <span className="tabla__principal" title="De otro vendedor: solo ves el renglón">
                          <Lock aria-hidden className="icono-linea" /> {c.cliente_nombre}
                        </span>
                      )}
                      <span className="tabla__secundario">
                        {c.multisitio ? `${c.multisitio.sitio} · ` : ''}
                        {ETIQUETA_TIPO[c.tipo]} · {c.ciudad} · {c.etapa === 'final' ? 'Final' : 'Precotización'}
                        {c.tarifa ? ` · ${c.tarifa}` : ''}
                      </span>
                    </td>
                    <td>
                      {c.vendedor_nombre}
                      {hechaPor(c) && <span className="tabla__secundario">{hechaPor(c)}</span>}
                    </td>
                    <td>
                      <Chip tono={tonoEstado(c.estado)}>{ETIQUETA_ESTADO[c.estado]}</Chip>
                      <Seguimiento fecha={c.proximo_seguimiento} />
                    </td>
                    <td className="num">{c.kwp_recomendada === null ? '—' : kwp(c.kwp_recomendada).replace(' kWp', '')}</td>
                    <td className="num cifra-tabla">{dinero(c.contado_recomendada)}</td>
                    <td className="num">{usdW(c.contado_usd_por_w)}</td>
                    {veSemaforo && (
                      <td>
                        <PuntoSemaforo color={c.semaforo_margen ?? null} />
                      </td>
                    )}
                    {veCostos && (
                      <>
                        <td className="num">{pct1(c.margen_pct)}</td>
                        <td className="num">{c.utilidad_sin_iva === null || c.utilidad_sin_iva === undefined ? '—' : dinero(c.utilidad_sin_iva)}</td>
                      </>
                    )}
                    <td>
                      <Rojos n={c.rojos_pendientes} />
                      <SinRespuesta dias={c.dias_sin_respuesta} />
                    </td>
                    <td className="tabla__fecha">{fechaHoraCorta(c.actualizada_en)}</td>
                    {conAcciones && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <BorradaPor c={c} />
                        {accion(c)}
                      </td>
                    )}
                    <td className="tabla__flecha">
                      <ChevronRight aria-hidden />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
